import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import StatusBadge from '@/components/ui/StatusBadge';
import Toast from '@/components/ui/Toast';
import { ApiError } from '@/lib/api';
import { usePerms } from '@/lib/perms';
import { OPEN_STATUSES, ordersApi, poTone, type OrdersPayload } from '@/lib/orders';
import { aed, num, shortDate } from '@/lib/format';

const statuses = ['All', 'Draft', 'Sent', 'PartiallyPickedUp', 'PickedUp', 'Closed', 'Cancelled'] as const;
type StatusFilter = (typeof statuses)[number];

/** Заказы поставщикам на реальных данных Zoho Books (решение 25.10.2026): список, сводка, загрузка из Zoho администратором. */
export default function PurchaseOrdersPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { action } = usePerms();
  const [filter, setFilter] = useState<StatusFilter>('All');
  const [query, setQuery] = useState('');
  const [data, setData] = useState<OrdersPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await ordersApi.list());
      setError(null);
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!data?.sync || data.sync.status !== 'running') return;
    const id = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(id);
  }, [data?.sync, load]);

  const orders = useMemo(() => data?.orders ?? [], [data]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((po) => (filter === 'All' || po.fulfillmentStatus === filter) && (!q || po.poNumber.toLowerCase().includes(q) || (po.partnerName ?? '').toLowerCase().includes(q) || (po.reference ?? '').toLowerCase().includes(q)));
  }, [orders, filter, query]);
  const openValue = orders.filter((po) => OPEN_STATUSES.includes(po.fulfillmentStatus)).reduce((s, po) => s + po.total, 0);
  const closedValue = orders.filter((po) => po.fulfillmentStatus === 'Closed' || po.fulfillmentStatus === 'PickedUp').reduce((s, po) => s + po.total, 0);
  const statusLabel = (s: string) => (s === 'All' ? t('common.all') : t(`status.${s}`, s));
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

  const sync = async () => {
    setBusy(true);
    try {
      await ordersApi.startSync(12);
      setToast(t('ord.syncStarted'));
      window.setTimeout(() => void load(), 1500);
    } catch (e) {
      const code = (e as ApiError).code;
      setToast(code === 'zoho_not_configured' ? t('ord.zohoNotConfigured') : code === 'already_running' ? t('ord.syncRunning') : t('onb.error', { code }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('po.eyebrow')}
        title={t('po.title')}
        description={t('ord.description')}
        actions={
          <>
            {data?.sync && (
              <span className="text-[11px] text-foreground-500">
                {data.sync.status === 'running' ? t('ord.syncRunning') : t('ord.lastSync', { date: fmt(data.sync.finishedAt), imported: data.sync.imported, skipped: data.sync.skipped })}
              </span>
            )}
            {action('zoho_sync') && (
              <button type="button" onClick={sync} disabled={busy || data?.sync?.status === 'running'} className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50">
                <i className="ri-refresh-line text-[14px]" /> {t('ord.syncNow')}
              </button>
            )}
          </>
        }
      />

      {error && <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{t('onb.error', { code: error })}</p>}
      {data && !data.zohoConfigured && <p className="mb-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-[12px] text-accent-900">{t('ord.zohoNotConfigured')}</p>}
      {data?.sync?.note && <p className="mb-3 text-[11.5px] text-foreground-500">{t('ord.unmatched')}: {data.sync.note.replace(/^[^:]*:\s*/, '')}</p>}

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: t('po.openValue'), value: aed(openValue), icon: 'ri-file-list-3-line' },
          { label: t('po.inProgress'), value: String(orders.filter((p) => OPEN_STATUSES.includes(p.fulfillmentStatus)).length), icon: 'ri-truck-line' },
          { label: t('po.deliveredValue'), value: aed(closedValue), icon: 'ri-checkbox-circle-line' },
          { label: t('po.unitsOnOrder'), value: num(orders.filter((p) => OPEN_STATUSES.includes(p.fulfillmentStatus)).reduce((s, p) => s + p.units, 0)), icon: 'ri-archive-2-line' },
        ].map((s) => (
          <div key={s.label} className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div>
              <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-500">{s.label}</p>
              <p className="tnum mt-1 text-base font-semibold text-foreground-950">{s.value}</p>
            </div>
            <i className={`${s.icon} text-[16px] text-foreground-300`} />
          </div>
        ))}
      </div>

      <Panel padded={false}>
        <div className="flex flex-col gap-3 border-b border-background-200 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-1 rounded-md border border-background-200 bg-background-100 p-1">
            {statuses.map((s) => (
              <button key={s} type="button" onClick={() => setFilter(s)} className={`cursor-pointer whitespace-nowrap rounded px-2 py-1 text-[11px] font-medium transition-colors ${filter === s ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'}`}>
                {statusLabel(s)}
                <span className="tnum ml-1 text-foreground-400">{s === 'All' ? orders.length : orders.filter((o) => o.fulfillmentStatus === s).length}</span>
              </button>
            ))}
          </div>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('ord.search')} className="h-8 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12px] outline-none focus:border-primary-400 md:w-64" />
        </div>

        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[900px] border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/70 text-left text-[10.5px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('po.colPo')}</th>
                <th className="px-3 py-2.5 font-medium">{t('po.colPartner')}</th>
                <th className="px-3 py-2.5 font-medium">{t('ord.colReference')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('po.colLines')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('ord.colTotal')}</th>
                <th className="px-3 py-2.5 font-medium">{t('ord.colOrdered')}</th>
                <th className="px-3 py-2.5 font-medium">{t('ord.colExpected')}</th>
                <th className="px-4 py-2.5 font-medium">{t('po.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((po) => (
                <tr key={po.poNumber} onClick={() => navigate(`/purchase-orders/${encodeURIComponent(po.poNumber)}`)} className="group cursor-pointer border-b border-background-200/70 transition-colors last:border-0 hover:bg-primary-50/40">
                  <td className="px-4 py-3">
                    <p className="tnum font-semibold text-foreground-900 group-hover:text-primary-700">{po.poNumber}</p>
                    <p className="text-[10.5px] text-foreground-400">{t('po.units', { count: po.units })}</p>
                  </td>
                  <td className="px-3 py-3 text-foreground-700">{po.partnerName}</td>
                  <td className="max-w-[220px] truncate px-3 py-3 text-foreground-600">{po.reference || '—'}</td>
                  <td className="tnum px-3 py-3 text-right text-foreground-700">{po.lines}</td>
                  <td className="tnum px-3 py-3 text-right font-semibold text-foreground-950">{aed(po.total)}</td>
                  <td className="tnum px-3 py-3 text-foreground-600">{po.orderedAt ? shortDate(po.orderedAt) : '—'}</td>
                  <td className="tnum px-3 py-3 text-foreground-600">{po.expectedAt ? shortDate(po.expectedAt) : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <StatusBadge tone={poTone(po.fulfillmentStatus) as 'primary' | 'secondary' | 'accent' | 'neutral' | 'danger'}>{t(`status.${po.fulfillmentStatus}`, po.fulfillmentStatus)}</StatusBadge>
                      <i className="ri-arrow-right-s-line text-[15px] text-foreground-300 group-hover:text-primary-500" />
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                    {data && orders.length === 0 ? t('ord.emptyAll') : t('po.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-background-200 px-4 py-2.5 text-[11px] text-foreground-400">
          <span>{t('po.footer1', { shown: rows.length, total: orders.length })}</span>
          <span>{t('ord.footerZoho')}</span>
        </div>
      </Panel>
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
